import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, PopupData, Service } from 'webapp-types';
import { AppEnv } from 'webapp/enums/enums';
import { copyObject, copyToClipboard, uniq } from 'webapp/utils/common';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import SubmitButtonComponent from 'webapp/components/submit-button';

const LANGSMITH_PROJECT_ID_PRODUCTION = '499e47c2-280a-4979-a1ba-0b83b210e82b';
const LANGSMITH_PROJECT_ID_DEVELOP = 'befe426b-a5cd-433e-84a2-1d1287b90a8a';
// The thread viewer reads a thread from whichever LangSmith project the link names, with the reader's own
// LangSmith key, so the production deployment opens develop threads too.
const THREAD_VIEWER_URL = 'https://agentic-service.databox.com/threads';

export default class CommonPopupDebuggingComponent extends PopupInnerModalComponent<PopupData.Common.Debugging> {
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('account') declare accountService: Service.Account;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('ai-chat') declare aiChatService: Service.AIChat;

	@tracked isMetricInfoLoading = false;
	@tracked metricInfo: APIData.PlatformAdmin.MetricsInfo | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		if (this.loadBlockDebugInfo) {
			this.isMetricInfoLoading = true;
			void this.mmBlocksService.getBlockDebugInfo(this.blockId!).then((reply) => {
				this.metricInfo = reply;
				this.isMetricInfoLoading = false;
			});
		}
	}

	get blockId() {
		return this.popupData.blockId;
	}

	get loadBlockDebugInfo() {
		// we also show debug popup for databox-email users
		// but we won't show block debug info (bcos of BE validation)
		return (
			this.blockId &&
			(this.currentUserService.loggedInAsPowerUser ||
				this.currentUserService.isPowerUser ||
				this.currentUserService.isPlatformAdmin)
		);
	}

	@cached
	get accountInfo() {
		const plan = this.billingService.activePlan,
			user = this.currentUserService.model;

		const data: Record<string, string | number | null | undefined> = {
			Account: `[${this.accountService.id}] ${this.accountService.company}`,
		};

		if (this.accountService.isAgency) {
			data.Account += ' (organization)';
		} else if (this.accountService.isClient) {
			data.Account += ' (account)';
		}

		if (user) {
			data.User = `[${user.id}] ${user.email}`;
		}

		if (plan) {
			data.Plan = `${plan.name} (${plan.id})`;
		}

		data.Time = moment.utc().format();
		data.Url = window.location.href;
		data.PALoginLink = this.accountService.getPALoginLink();

		if (this.blockId) {
			data.BlockId = this.blockId;
		}

		if (this.threadId) {
			data.ChatThreadId = this.threadId;
			data.ThreadViewer = this.threadViewerUrl;
		}

		return data;
	}

	@cached
	get debuggingData() {
		let data = this.accountInfo;
		const metricInfo = this.metricInfo;
		if (metricInfo) {
			// when loaded, update the data shown
			data = copyObject(data); // creates a copy
			if (metricInfo.KibanaRequestId) {
				data.KibanaRequestId = metricInfo.KibanaRequestId;
			}
			if (metricInfo.BlockType) {
				data.BlockId += ` (${metricInfo.BlockType})`;
			}
			if (metricInfo.Info) {
				const metricKeys: string[] = [];
				this.metricInfoSections.forEach((sessionData) => {
					Object.values(sessionData.data || {}).forEach((metricData) => {
						metricKeys.push(metricData.Key);
					});
				});
				data.Metrickeys = uniq(metricKeys).join(', ');
			}
			// if (metricInfo.Info) {
			// 	data.Metrics = ''; // just for the looks
			// 	this.metricInfoSections.forEach((sessionData) => {
			// 		Object.keys(sessionData.data || {}).forEach((metricType: 'Current' | 'Compare') => {
			// 			const metricData = sessionData.data[metricType],
			// 				lineKey = `${sessionData.id} ${metricType}`;
			// 			let lineValue = `${metricData.Key} (${metricData.Range})`; // Key (Range) [MsId 1] [MdpcId 1]
			// 			// (metricData.DataConfigurations || []).forEach((dataConfig) => {
			// 			// 	lineValue += ` [MsId: ${dataConfig.MetricSettingId}] [MdpcId: ${dataConfig.MdpcId}]`;
			// 			// });
			// 			data[lineKey] = lineValue;
			// 		});
			// 	});
			// }
		}
		return data;
	}

	@cached
	get debuggingDataFormatted() {
		const debuggingData = this.debuggingData;
		return Object.keys(debuggingData)
			.map((prop) => {
				return prop + ': ' + debuggingData[prop];
			})
			.join('\n');
	}

	@cached
	get metricInfoSections() {
		type Section = NonNullable<APIData.PlatformAdmin.MetricsInfo['Info']>[string];
		const sections: { id: string; data: Section; dataFormatted: string }[] = [];
		const info = this.metricInfo?.Info;
		if (info) {
			Object.entries(info).forEach(([id, data]) => {
				const dataFormatted = JSON.stringify(data, null, 2);
				sections.push({
					id,
					data,
					dataFormatted,
				});
			});
		}
		return sections;
	}

	@action
	copy() {
		copyToClipboard(this.debuggingDataFormatted);
		this.infolayerService.ok('Copied to clipboard!');
	}

	get threadId(): string | null {
		return this.aiChatService.threadId;
	}

	get threadViewerUrl(): string | null {
		const threadId = this.threadId;
		if (!threadId) {
			return null;
		}

		const isProduction = window.env === AppEnv.PRODUCTION;
		const projectId = isProduction
			? LANGSMITH_PROJECT_ID_PRODUCTION
			: LANGSMITH_PROJECT_ID_DEVELOP;

		return `${THREAD_VIEWER_URL}#project=${projectId}&thread=${threadId}`;
	}

	@action
	goToThreadViewer() {
		if (!this.threadViewerUrl) {
			return;
		}

		window.open(this.threadViewerUrl, '_blank');
	}

	@action
	toggleSection(e: PointerEvent): void {
		const section = (e.target as HTMLElement).closest('.togglebox');
		if (section) {
			section.classList.toggle('open');
			this.reposition();
		}
	}
	<template>
		<div class="title">
			Debugging information
		</div>
		<div class="content">

			<pre>{{this.debuggingDataFormatted}}</pre>

			<div class="controls2">
				<div class="controls2__right">
					{{#if this.threadViewerUrl}}
						<SubmitButtonComponent
							@text="Thread viewer"
							@theme="blue-outlined"
							@noDelay={{true}}
							@onTrigger={{this.goToThreadViewer}}
						/>
					{{/if}}
					<SubmitButtonComponent
						@text="Copy"
						@noDelay={{true}}
						@onTrigger={{this.copy}}
					/>
				</div>
			</div>

			{{#if this.isMetricInfoLoading}}
				<p><br />Fetching metric info...</p>
			{{else}}
				<ul class="sections">
					{{#each this.metricInfoSections as |section|}}
						<li class="togglebox">
							<a class="toggler" {{on "click" this.toggleSection}}>
								<CommonUiIconComponent @name="ChevronDown" @size="xs" />
								{{section.id}}
							</a>
							<div class="ct"><pre>{{section.dataFormatted}}</pre></div>
						</li>
					{{/each}}
				</ul>
			{{/if}}

		</div>
	</template>
}
